import {
  Typography,
  Box,
  Autocomplete,
  TextField,
  Button,
} from "@mui/material";
import * as React from "react";
import theme from "../../../theme";
import { LoadingButton } from "@mui/lab";
import { getOilParams } from "../../../services/core-api";
import { Count } from "../../../interfaces/DataSupplierFacetCounts";
import { OilCriteria } from "../../../interfaces/OilCriteria";

/** Autodoc OEM recommendations — TecDoc Specification (1073) is primary; approvals as extras */
const OEM_GROUP_NAMES = [
  "Specification",
  "Manufacturer Approval",
  "Manufacturer Approval (licensed)",
  "Oil manufacturer recommendation",
];

/** Autodoc Specification dropdown */
const SPEC_GROUP_NAMES = [
  "ACEA specification",
  "ACEA specification (licensed)",
  "API specification",
  "API specification (licensed)",
  "ILSAC specification",
  "ILSAC specification (licensed)",
];

const mergeCriteria = (...lists: Array<OilCriteria[] | undefined>) => {
  const seen = new Set<string>();
  const merged: OilCriteria[] = [];
  for (const list of lists) {
    for (const item of list || []) {
      const key = `${item.criteriaId}:${item.rawValue}`;
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(item);
    }
  }
  return merged;
};

const fromGrouped = (
  grouped: Record<string, OilCriteria[]> = {},
  names: string[]
) => mergeCriteria(...names.map((name) => grouped[name] || []));

const fromFacets = (response: any, names: string[]): OilCriteria[] => {
  const facets = response?.criteriaFacets?.counts || [];
  const nameSet = new Set(names);
  const seen = new Set<string>();
  const out: OilCriteria[] = [];
  for (const facet of facets) {
    const meta = facet?.criteria || {};
    const desc: string = meta.criteriaDescription || "";
    if (!nameSet.has(desc)) continue;
    for (const vc of facet?.criteriaValueCounts || []) {
      const rawValue = vc?.rawValue;
      if (rawValue == null || rawValue === "") continue;
      const key = `${meta.criteriaId}:${rawValue}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        criteriaId: Number(meta.criteriaId),
        criteriaDescription: desc,
        criteriaAbbrDescription: meta.criteriaAbbrDescription || desc,
        criteriaType: meta.criteriaType || "A",
        keyTableNum: meta.keyTableNum ?? 0,
        keyTableType: meta.keyTableType || "",
        rawValue: String(rawValue),
        formattedValue: vc.formattedValue || String(rawValue),
        immediateDisplay: true,
        isMandatory: false,
        isInterval: false,
      });
    }
  }
  return out;
};

const formatSpecLabel = (option: OilCriteria) => {
  const label = option.formattedValue || option.rawValue || "";
  const desc = option.criteriaDescription || "";
  if (desc.startsWith("ACEA") && label && !/^ACEA\b/i.test(label)) {
    return `ACEA ${label}`;
  }
  if (desc.startsWith("API") && label && !/^API\b/i.test(label)) {
    return `API ${label}`;
  }
  if (desc.startsWith("ILSAC") && label && !/^ILSAC\b/i.test(label)) {
    return `ILSAC ${label}`;
  }
  return label;
};

const oemFromResponse = (response: any) =>
  mergeCriteria(
    fromFacets(response, OEM_GROUP_NAMES),
    fromGrouped(response?.groupedCriteria || {}, OEM_GROUP_NAMES)
  ).sort((a, b) =>
    (a.formattedValue || "").localeCompare(b.formattedValue || "")
  );

const specsFromResponse = (response: any) =>
  mergeCriteria(
    fromFacets(response, SPEC_GROUP_NAMES),
    fromGrouped(response?.groupedCriteria || {}, SPEC_GROUP_NAMES)
  ).sort((a, b) => formatSpecLabel(a).localeCompare(formatSpecLabel(b)));

const SelectEngineRequirements = () => {
  const frontendURL = process.env.REACT_APP_FRONTEND_URL;
  const [isLoadingData, setIsLoadingData] = React.useState(false);

  const [viscosities, setViscosities] = React.useState<OilCriteria[]>([]);
  const [selectedViscosity, setSelectedViscosity] =
    React.useState<OilCriteria | null>(null);

  const [brands, setBrands] = React.useState<Count[]>([]);
  const [selectedBrand, setSelectedBrand] = React.useState<Count | null>(null);

  const [oemApprovels, setOemApprovels] = React.useState<OilCriteria[]>([]);
  const [selectedOemApprovel, setSelectedOemApprovel] =
    React.useState<OilCriteria | null>(null);
  const [specifications, setSpecifications] = React.useState<OilCriteria[]>(
    []
  );
  const [selectedSpecification, setSelectedSpecification] =
    React.useState<OilCriteria | null>(null);

  const [page] = React.useState(1);

  const applyFromResponse = (
    response: any,
    opts: { updateBrands?: boolean; updateOem?: boolean; updateSpecs?: boolean } = {}
  ) => {
    const { updateBrands = false, updateOem = true, updateSpecs = true } = opts;
    if (updateBrands && response?.dataSupplierFacets?.counts) {
      setBrands(response.dataSupplierFacets.counts);
    }
    if (updateOem) {
      const oem = oemFromResponse(response);
      if (oem.length > 0) setOemApprovels(oem);
    }
    if (updateSpecs) {
      const specs = specsFromResponse(response);
      if (specs.length > 0) setSpecifications(specs);
    }
  };

  React.useEffect(() => {
    let active = true;
    (async () => {
      try {
        const response = await getOilParams({ page });
        if (!active || !response) return;
        const saeViscosity = response.groupedCriteria?.["SAE viscosity class"];
        if (saeViscosity) setViscosities(saeViscosity);
        applyFromResponse(response, { updateBrands: true });
      } catch (error) {}
    })();
    return () => {
      active = false;
    };
  }, [page]);

  const handleVicosityChange = async (
    event: React.ChangeEvent<{}>,
    viscosity: OilCriteria | null
  ) => {
    if (!viscosity) return;
    setSelectedViscosity(viscosity);
    setSelectedOemApprovel(null);
    setSelectedSpecification(null);
    const updatedFilters = [
      {
        criteriaId: Number(viscosity.criteriaId),
        rawValue: viscosity.rawValue,
      },
    ];
    try {
      const response = await getOilParams({
        ...(selectedBrand && { manufacturer: selectedBrand.dataSupplierId }),
        criteria: updatedFilters,
        page,
      });
      if (response) {
        applyFromResponse(response, { updateBrands: true });
      }
    } catch (error) {}
  };

  const handleBrandChange = async (
    event: React.ChangeEvent<{}>,
    brand: Count | null
  ) => {
    if (!brand) return;
    setSelectedBrand(brand);
    setSelectedOemApprovel(null);
    setSelectedSpecification(null);
    // Never pass viscosity as searchQuery — that removes ACEA from TecDoc results
    const viscosityFilters = selectedViscosity
      ? [
          {
            criteriaId: Number(selectedViscosity.criteriaId),
            rawValue: selectedViscosity.rawValue,
          },
        ]
      : [];
    try {
      const response = await getOilParams({
        manufacturer: brand.dataSupplierId,
        ...(viscosityFilters.length ? { criteria: viscosityFilters } : {}),
        page,
      });
      if (response) {
        applyFromResponse(response);
      }
    } catch (error) {}
  };

  const handleOemApprovelChange = (
    event: React.ChangeEvent<{}>,
    oemApprovel: OilCriteria | null
  ) => {
    if (!oemApprovel) return;
    setSelectedOemApprovel(oemApprovel);
    setSelectedSpecification(null);
    // Keep Specification (ACEA/API/ILSAC) list from viscosity/brand — do not re-filter
  };

  const handleSpecificationChange = (
    event: React.ChangeEvent<{}>,
    specification: OilCriteria | null
  ) => {
    if (!specification) return;
    setSelectedSpecification(specification);
  };

  const handleSubmitData = () => {
    setIsLoadingData(true);
    const brandId = selectedBrand?.dataSupplierId;
    const criteriaParam = (id: any, value: any) =>
      id != null && value != null && value !== ""
        ? `&criteria=${id}:${encodeURIComponent(String(value))}`
        : "";
    const dynamicUrl =
      `${frontendURL}/search-oil?` +
      `${brandId ? `&brand=${brandId}` : ""}` +
      `&category=1862` +
      criteriaParam(selectedViscosity?.criteriaId, selectedViscosity?.rawValue) +
      criteriaParam(
        selectedOemApprovel?.criteriaId,
        selectedOemApprovel?.rawValue
      ) +
      criteriaParam(
        selectedSpecification?.criteriaId,
        selectedSpecification?.rawValue
      );
    setIsLoadingData(false);
    window.location.href = dynamicUrl;
  };

  return (
    <Box
      sx={{
        padding: "10px",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        flex: 1,
      }}
    >
      <Typography
        variant="body1"
        align="left"
        sx={{
          fontSize: "13px",
          color: theme.palette.text.primary,
          m: 1,
          display: "flex",
          alignItems: "center",
        }}
      >
        Select Brands and Speed rating
      </Typography>
      <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
        <Autocomplete
          options={viscosities}
          getOptionLabel={(option) => option.formattedValue || ""}
          isOptionEqualToValue={(a, b) =>
            a.criteriaId === b.criteriaId && a.rawValue === b.rawValue
          }
          renderOption={(props, option) => {
            return (
              <Box
                component="li"
                sx={{
                  fontFamily: "Inter",
                  "&:hover": {
                    backgroundColor: "#dcdcdc",
                  },
                  color: theme.palette.text.primary,
                  padding: "10px",
                  fontSize: "13px",
                }}
                {...props}
              >
                {option.formattedValue}
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="SAE viscosity grade"
              InputLabelProps={{
                ...params.InputLabelProps,
                sx: {
                  color: theme.palette.text.primary,
                  fontSize: "13px",
                  height: "100%",
                  top: "-4px",
                  transform: "translate(24px, 12px), scale(1) !important",
                },
              }}
              InputProps={{
                ...params.InputProps,
                sx: {
                  borderRadius: 0,
                  fontSize: "13px",
                  height: "40px",
                },
              }}
            />
          )}
          value={selectedViscosity}
          onChange={handleVicosityChange}
          sx={{
            flex: 1,
            marginTop: "5px",
            width: "100%",
            height: "40px",
            backgroundColor: "#fff",
            ".MuiInputBase-input": {
              height: "40px",
              marginTop: "0px",
              marginBottom: "0px",
              paddingTop: "0px",
              paddingBottom: "0px",
            },
          }}
        />
        <Autocomplete
          options={brands}
          getOptionLabel={(option) => option.mfrName || ""}
          isOptionEqualToValue={(a, b) =>
            a.dataSupplierId === b.dataSupplierId
          }
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              sx={{
                fontFamily: "Inter",
                "&:hover": {
                  backgroundColor: "#dcdcdc",
                },
                color: theme.palette.text.primary,
                padding: "10px",
                fontSize: "13px",
              }}
            >
              {option.mfrName}
            </Box>
          )}
          onChange={handleBrandChange}
          value={selectedBrand}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Brand"
              InputLabelProps={{
                ...params.InputLabelProps,
                sx: {
                  color: theme.palette.text.primary,
                  fontSize: "13px",
                  height: "100%",
                  top: "-4px",
                  transform: "translate(24px, 12px), scale(1) !important",
                },
              }}
              InputProps={{
                ...params.InputProps,
                sx: {
                  borderRadius: 0,
                  height: "40px",
                  fontSize: "13px",
                },
              }}
            />
          )}
          sx={{
            flex: 1,
            marginTop: "5px",
            width: "100%",
            height: "40px",
            backgroundColor: "#fff",
            ".MuiInputBase-input": {
              height: "40px",
              marginTop: "0px",
              marginBottom: "0px",
              paddingTop: "0px",
              paddingBottom: "0px",
            },
          }}
        />
      </Box>
      <Typography
        variant="body1"
        align="left"
        sx={{
          fontSize: "13px",
          color: theme.palette.text.primary,
          m: 1,
          display: "flex",
          alignItems: "center",
        }}
      >
        Select OEM approval and Specification
      </Typography>
      <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
        <Autocomplete
          options={oemApprovels}
          getOptionLabel={(option) => option.formattedValue || ""}
          isOptionEqualToValue={(a, b) =>
            a.criteriaId === b.criteriaId && a.rawValue === b.rawValue
          }
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              sx={{
                fontFamily: "Inter",
                "&:hover": {
                  backgroundColor: "#dcdcdc",
                },
                color: theme.palette.text.primary,
                padding: "10px",
                fontSize: "13px",
              }}
            >
              {option.formattedValue}
            </Box>
          )}
          onChange={handleOemApprovelChange}
          value={selectedOemApprovel}
          renderInput={(params) => (
            <TextField
              {...params}
              label="OEM approval"
              InputLabelProps={{
                ...params.InputLabelProps,
                sx: {
                  color: theme.palette.text.primary,
                  fontSize: "13px",
                  height: "100%",
                  top: "-4px",
                  transform: "translate(24px, 12px), scale(1) !important",
                },
              }}
              InputProps={{
                ...params.InputProps,
                sx: {
                  borderRadius: 0,
                  height: "40px",
                  fontSize: "13px",
                },
              }}
            />
          )}
          sx={{
            flex: 1,
            width: "100%",
            height: "40px",
            backgroundColor: "#fff",
            ".MuiInputBase-input": {
              height: "40px",
              marginTop: "0px",
              marginBottom: "0px",
              paddingTop: "0px",
              paddingBottom: "0px",
            },
          }}
        />
        <Autocomplete
          options={specifications}
          getOptionLabel={formatSpecLabel}
          isOptionEqualToValue={(a, b) =>
            a.criteriaId === b.criteriaId && a.rawValue === b.rawValue
          }
          onChange={handleSpecificationChange}
          value={selectedSpecification}
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              sx={{
                fontFamily: "Inter",
                "&:hover": {
                  backgroundColor: "#dcdcdc",
                },
                color: theme.palette.text.primary,
                padding: "10px",
                fontSize: "13px",
              }}
            >
              {formatSpecLabel(option)}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Specification"
              InputLabelProps={{
                ...params.InputLabelProps,
                sx: {
                  color: theme.palette.text.primary,
                  fontSize: "13px",
                  height: "100%",
                  top: "-4px",
                  transform: "translate(24px, 12px), scale(1) !important",
                },
              }}
              InputProps={{
                ...params.InputProps,
                sx: {
                  borderRadius: 0,
                  height: "40px",
                  fontSize: "13px",
                },
              }}
            />
          )}
          sx={{
            flex: 1,
            width: "100%",
            height: "40px",
            backgroundColor: "#fff",
            ".MuiInputBase-input": {
              height: "40px",
              marginTop: "0px",
              marginBottom: "0px",
              paddingTop: "0px",
              paddingBottom: "0px",
            },
          }}
        />
      </Box>
      <Box sx={{ marginTop: "auto", pt: 1 }}>
        {isLoadingData ? (
          <LoadingButton
            loading
            variant="contained"
            sx={{
              height: "38px",
              width: "100%",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            Submit
          </LoadingButton>
        ) : (
          <Button
            disabled={
              !selectedViscosity &&
              !selectedBrand &&
              !selectedOemApprovel &&
              !selectedSpecification
            }
            sx={{
              width: "100%",
              textTransform: "capitalize",
              borderRadius: 0,
              backgroundColor: "secondary.main",
              color: "text.secondary",
              ":hover": {
                backgroundColor: "secondary.main",
                color: "#fff",
              },
            }}
            onClick={handleSubmitData}
          >
            Search
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default SelectEngineRequirements;
